import { db } from '../db';
import { downloadBlob } from './backup';
import { getSettings } from '../stores/settings.svelte.ts';
import { getChildById } from '../stores/children.svelte.ts';
import { mlToOz, gramsToLbs } from './units.ts';

function toCsvRow(values: (string | number | undefined)[]): string {
  return values.map(v => {
    const str = v == null ? '' : String(v);
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  }).join(',');
}

function childName(id: string): string {
  return getChildById(id)?.name ?? '';
}

export async function exportSessionsCSV(childIds: string[]): Promise<void> {
  const imperial = getSettings().units === 'imperial';
  const includeChild = childIds.length > 1;
  const idSet = new Set(childIds);
  const all = await db.sessions.filter(s => !s.deletedAt && idSet.has(s.childId)).toArray();
  const volHeader = imperial ? 'Volume (oz)' : 'Volume (mL)';
  const headers = [
    ...(includeChild ? ['Child'] : []),
    'Date', 'Time', 'Type', 'Side', 'Duration (min)', volHeader, 'Source', 'Note',
  ];
  const rows = all.map(s => {
    const vol = s.volumeMl != null ? (imperial ? mlToOz(s.volumeMl) : s.volumeMl) : '';
    return toCsvRow([
      ...(includeChild ? [childName(s.childId)] : []),
      new Date(s.startTime).toLocaleDateString(),
      new Date(s.startTime).toLocaleTimeString(),
      s.type,
      s.side || '',
      s.durationMs ? Math.round(s.durationMs / 60000) : '',
      vol,
      s.source || '',
      s.note || '',
    ]);
  });

  const csv = [toCsvRow(headers), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  downloadBlob(blob, `mamaobaby-sessions-${new Date().toISOString().split('T')[0]}.csv`);
}

export async function exportDiapersCSV(childIds: string[]): Promise<void> {
  const includeChild = childIds.length > 1;
  const idSet = new Set(childIds);
  const all = await db.diapers.filter(d => !d.deletedAt && idSet.has(d.childId)).toArray();
  const headers = [
    ...(includeChild ? ['Child'] : []),
    'Date', 'Time', 'Type', 'Note',
  ];
  const rows = all.map(d => toCsvRow([
    ...(includeChild ? [childName(d.childId)] : []),
    new Date(d.time).toLocaleDateString(),
    new Date(d.time).toLocaleTimeString(),
    d.type,
    d.note || '',
  ]));

  const csv = [toCsvRow(headers), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  downloadBlob(blob, `mamaobaby-diapers-${new Date().toISOString().split('T')[0]}.csv`);
}

export async function exportWeightsCSV(childIds: string[]): Promise<void> {
  const imperial = getSettings().units === 'imperial';
  const includeChild = childIds.length > 1;
  const idSet = new Set(childIds);
  const all = await db.weights.filter(w => !w.deletedAt && idSet.has(w.childId)).toArray();
  const weightHeader = imperial ? 'Weight (lbs)' : 'Weight (g)';
  const headers = [
    ...(includeChild ? ['Child'] : []),
    'Date', weightHeader, 'Note',
  ];
  const rows = all.map(w => toCsvRow([
    ...(includeChild ? [childName(w.childId)] : []),
    new Date(w.date).toLocaleDateString(),
    imperial ? gramsToLbs(w.weightGrams) : w.weightGrams,
    w.note || '',
  ]));

  const csv = [toCsvRow(headers), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  downloadBlob(blob, `mamaobaby-weights-${new Date().toISOString().split('T')[0]}.csv`);
}

export async function exportStorageCSV(childIds: string[]): Promise<void> {
  const imperial = getSettings().units === 'imperial';
  const includeChild = childIds.length > 1;
  const idSet = new Set(childIds);
  const all = await db.storageEntries.filter(e => !e.deletedAt && idSet.has(e.childId)).toArray();
  const volHeader = imperial ? 'Volume (oz)' : 'Volume (mL)';
  const remHeader = imperial ? 'Remaining (oz)' : 'Remaining (mL)';
  const headers = [
    ...(includeChild ? ['Child'] : []),
    'Stored Date', 'Location', volHeader, remHeader, 'Expires', 'Depleted', 'Note',
  ];
  const rows = all.map(e => toCsvRow([
    ...(includeChild ? [childName(e.childId)] : []),
    new Date(e.storedAt).toLocaleDateString(),
    e.location,
    imperial ? mlToOz(e.originalVolumeMl) : e.originalVolumeMl,
    imperial ? mlToOz(e.remainingVolumeMl) : e.remainingVolumeMl,
    new Date(e.expiresAt).toLocaleDateString(),
    e.depletedAt ? new Date(e.depletedAt).toLocaleDateString() : '',
    e.note || '',
  ]));

  const csv = [toCsvRow(headers), ...rows].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  downloadBlob(blob, `mamaobaby-storage-${new Date().toISOString().split('T')[0]}.csv`);
}
